import _parseFloat from "@babel/runtime-corejs3/core-js/parse-float";
import PageStepLayout from "@Client/@Modules/application-standalone/components/layout/page-step-layout.vue";
import PageSectionGroupLayout from "@Client/@Modules/application-standalone/components/layout/page-section-group-layout.vue";
import { useScrollTopOnLoad } from "@Shared/@Composables/generic/useScrollTopOnLoad";
import { computed, defineComponent, onBeforeUnmount, onMounted, ref, toRefs, watch } from "@vue/composition-api";
import RadioField from "@Shared/components/form-elements/fields/radio-field.vue";
import CurrencyField from "@Shared/components/form-elements/fields/currency-field.vue";
import PercentageField from "@Shared/components/form-elements/fields/percentage-field.vue";
import DateField from "@Shared/components/form-elements/fields/date-field.vue";
import SelectField from "@Shared/components/form-elements/fields/select-field.vue";
import { useBindApplication } from "@Client/@Composables/application-standalone/useBindApplication";
import { APPLICATION_FIELD_OPTIONS } from "@Shared/@Utils/application-defaults-utils/global-field-options";
import { APPLICATION_FLOW } from "@Shared/@Types/application/application-enums";
import { cloneDeep, get } from "lodash";
import * as ApplicationStandAloneActions from "@Client/@Modules/application-standalone/store/application-standalone-actions";
import AddressFieldGroup from "@Shared/components/form-elements/field-groups/address-field-group.vue";
import { GLOBAL_APPLICATION_FIELD_OPTIONS } from "@Shared/@Utils/application-defaults-utils/global-field-options";
import { useI18n } from "vue-i18n-composable";
import { goalsPageValidator } from "@Client/@Composables/application-standalone/validations/goalsPageValidator";
import { useApplicationStandaloneStoreGetters } from "@Client/@Modules/application-standalone/store/application-standalone-helpers";
import * as ApplicationStandaloneGetters from "@Client/@Modules/application-standalone/store/application-standalone-getters";
import { useApplicationStandaloneStoreActions } from "@Client/@Modules/application-standalone/store/application-standalone-helpers";
import NotificationPopover from "@Shared/components/general/notification-popover.vue";
import { goalsBindingsAndVisibilities } from "@Client/@Composables/application-standalone/pages/goals/goalsBindingsAndVisibilities";
import { PAGE_TOGGLER_TARGET, usePageTogglers } from "@Client/@Composables/application-standalone/page-helpers/usePageToggler";
export default defineComponent({
  name: "GoalsPage",
  components: {
    PageStepLayout,
    PageSectionGroupLayout,
    RadioField,
    CurrencyField,
    DateField,
    PercentageField,
    SelectField,
    AddressFieldGroup,
    NotificationPopover
  },
  props: {
    showErrors: {
      type: Number,
      default: 0
    }
  },
  setup(props, {
    emit
  }) {
    const {
      showErrors
    } = toRefs(props);
    const addressFieldComponent = ref();
    useScrollTopOnLoad();
    const {
      firstFlowSelectionOptions,
      secondFlowSelectionOptions
    } = APPLICATION_FIELD_OPTIONS();
    const {
      handlePageTogglerNavigation
    } = usePageTogglers();
    const {
      bindApplication
    } = useBindApplication();
    const {
      t
    } = useI18n();
    const flowSelection = bindApplication("[flow]");
    const isPurchaseSelected = computed(() => flowSelection.value === APPLICATION_FLOW.PURCHASE);
    const isPreApprovalSelected = computed(() => flowSelection.value === APPLICATION_FLOW.PRE_APPROVAL);
    const showSecondFlowSelection = computed(() => isPurchaseSelected.value || isPreApprovalSelected.value);
    const {
      canadaProvinceOptions
    } = GLOBAL_APPLICATION_FIELD_OPTIONS;
    const conditionalFirstFlowSelectionOptions = computed(() => {
      const selection = cloneDeep(firstFlowSelectionOptions);
      selection.forEach(option => {
        option["hasTooltip"] = option.value === APPLICATION_FLOW.REFINANCE || option.value === APPLICATION_FLOW.SWITCH_TRANSFER;
        if (option.value === APPLICATION_FLOW.REFINANCE) option["tooltip"] = t("application-standalone.pages.goals.refinanceInformationTooltip");
        if (option.value === APPLICATION_FLOW.SWITCH_TRANSFER) option["tooltip"] = t("application-standalone.pages.goals.switchTransferInformationTooltip");
      });
      if (isPurchaseSelected.value) {
        selection[0].value = APPLICATION_FLOW.PURCHASE;
      }
      if (isPreApprovalSelected.value) {
        selection[0].value = APPLICATION_FLOW.PRE_APPROVAL;
      }
      return selection;
    });
    const flowSelectionMade = computed(() => !!flowSelection.value);
    const goal = computed(() => t(`application-flow.flows.${flowSelection.value}`).toString().toLowerCase());
    const closingDateQuestion = computed(() => {
      if (isPurchaseSelected.value) return t("application-standalone.questions.purchaseClosingDate");
      if (isPreApprovalSelected.value) return t("application-standalone.questions.preApprovalClosingDate");
      return t("application-standalone.questions.closingDate", {
        goal: goal.value
      });
    });
    const {
      UPDATE_APPLICATION_FIELD
    } = useApplicationStandaloneStoreActions([ApplicationStandAloneActions.UPDATE_APPLICATION_FIELD]);
    const downPaymentPercentage = ref(0);
    const downpaymentIndex = ref(0);
    const {
      IS_FILL_OUT,
      MORTGAGE_DETAILS_REQUESTED,
      PROPERTY_REQUESTED
    } = useApplicationStandaloneStoreGetters([ApplicationStandaloneGetters.IS_FILL_OUT, ApplicationStandaloneGetters.MORTGAGE_DETAILS_REQUESTED, ApplicationStandaloneGetters.PROPERTY_REQUESTED]);
    const {
      bindings,
      visibilities
    } = goalsBindingsAndVisibilities(downpaymentIndex);
    const {
      propertyDetailsPurchasePrice,
      propertyAddress,
      propertyDetailsAddressProvince,
      mortgageDetailsDownpaymentAmount,
      mortgageDetailsClosingDate
    } = bindings;
    const {
      propertyDetailsPurchasePriceVisible,
      propertyAddressVisible,
      propertyAddressProvinceVisible,
      mortgageDetailsClosingDateVisible,
      mortgageDetailsDownpaymentAmountVisible
    } = visibilities;
    watch([mortgageDetailsDownpaymentAmount, propertyDetailsPurchasePrice], () => {
      if (propertyDetailsPurchasePrice.value) {
        const purchasePrice = _parseFloat(propertyDetailsPurchasePrice.value);
        downPaymentPercentage.value = _parseFloat(mortgageDetailsDownpaymentAmount.value) * 100 / purchasePrice;
      }
    }, {
      immediate: true
    });
    watch(downPaymentPercentage, () => {
      if (propertyDetailsPurchasePrice.value) {
        const purchasePrice = _parseFloat(propertyDetailsPurchasePrice.value);
        UPDATE_APPLICATION_FIELD({
          path: `[mortgage-details][down-payment][${downpaymentIndex.value}][amount]`,
          value: (purchasePrice * downPaymentPercentage.value / 100).toString()
        });
      }
    });
    const {
      v$
    } = goalsPageValidator();
    watch(propertyDetailsPurchasePrice, () => {
      var _v$$value, _v$$value$propertyDet;
      v$ == null ? void 0 : (_v$$value = v$.value) == null ? void 0 : (_v$$value$propertyDet = _v$$value.propertyDetailsPurchasePrice) == null ? void 0 : _v$$value$propertyDet.$touch();
    });
    const handleUseAsDownpayment = () => {};
    const handleNavigateNext = () => {
      emit("navigate-next");
    };
    const handleNavigateBack = () => {
      emit("navigate-back");
    };
    const hideFlowQuestions = computed(() => IS_FILL_OUT.value && (MORTGAGE_DETAILS_REQUESTED.value || PROPERTY_REQUESTED.value));
    watch(showErrors, newVal => {
      if (newVal) {
        var _v$$value2, _v$$value3, _v$$value3$propertyAd, _addressFieldComponen;
        v$ == null ? void 0 : (_v$$value2 = v$.value) == null ? void 0 : _v$$value2.$touch();
        if (v$ != null && (_v$$value3 = v$.value) != null && (_v$$value3$propertyAd = _v$$value3.propertyAddress) != null && _v$$value3$propertyAd.$invalid) (_addressFieldComponen = addressFieldComponent.value) == null ? void 0 : _addressFieldComponen.openManualAddress(true);
      }
    });
    onMounted(() => {
      handlePageTogglerNavigation({
        target: PAGE_TOGGLER_TARGET.INFORMATION
      });
      emit("update:showInformationPage", true);
    });
    onBeforeUnmount(() => {
      emit("show-errors", 0);
    });
    const getValidator = path => get(v$.value, path, null);
    return {
      flowSelection,
      propertyDetailsPurchasePrice,
      mortgageDetailsClosingDate,
      propertyAddress,
      propertyDetailsAddressProvince,
      conditionalFirstFlowSelectionOptions,
      secondFlowSelectionOptions,
      canadaProvinceOptions,
      mortgageDetailsDownpaymentAmount,
      showSecondFlowSelection,
      flowSelectionMade,
      isPurchaseSelected,
      isPreApprovalSelected,
      downPaymentPercentage,
      handleUseAsDownpayment,
      handleNavigateNext,
      handleNavigateBack,
      propertyDetailsPurchasePriceVisible,
      propertyAddressVisible,
      propertyAddressProvinceVisible,
      mortgageDetailsClosingDateVisible,
      mortgageDetailsDownpaymentAmountVisible,
      goal,
      v$,
      closingDateQuestion,
      hideFlowQuestions,
      getValidator,
      addressFieldComponent
    };
  }
});